import React from "react";
import {Sequence, Audio, staticFile} from "remotion";

/**
 * SFX Helper — Auto-places sound effects based on scene timing.
 * Follows SFX-GUIDE.md patterns: whoosh on transitions, pop on reveals,
 * impact on big moments, riser before climax.
 */

// SFX library paths
const SFX = {
  whoosh: ["sfx/whoosh/whoosh-01.mp3", "sfx/whoosh/whoosh-02.mp3", "sfx/whoosh/whoosh-03.mp3"],
  pop: ["sfx/pop/pop-01.mp3", "sfx/pop/pop-02.mp3", "sfx/pop/pop-03.mp3"],
  impact: ["sfx/impact/impact-01.mp3", "sfx/impact/impact-02.mp3", "sfx/impact/impact-03.mp3"],
  riser: ["sfx/riser/riser-01.mp3", "sfx/riser/riser-02.mp3", "sfx/riser/riser-03.mp3"],
  glitch: ["sfx/glitch/glitch-01.mp3", "sfx/glitch/glitch-02.mp3", "sfx/glitch/glitch-03.mp3"],
  click: ["sfx/ui/click-01.mp3", "sfx/ui/click-02.mp3", "sfx/ui/click-03.mp3"],
  notification: ["sfx/notification/ping-01.mp3", "sfx/notification/ping-02.mp3", "sfx/notification/chime-01.mp3"],
  shimmer: ["sfx/shimmer/shimmer-01.mp3", "sfx/shimmer/shimmer-02.mp3", "sfx/shimmer/sparkle-01.mp3"],
  success: ["sfx/success/success-01.mp3", "sfx/success/level-up-01.mp3", "sfx/success/achievement-01.mp3"],
  cash: ["sfx/cash/coin-01.mp3", "sfx/cash/coin-02.mp3", "sfx/cash/register-01.mp3"],
  drop: ["sfx/drop/drop-01.mp3", "sfx/drop/drop-02.mp3", "sfx/drop/drop-03.mp3"],
} as const;

type SfxCategory = keyof typeof SFX;

function pick(category: SfxCategory, index: number): string {
  const arr = SFX[category];
  return arr[index % arr.length];
}

export interface SceneSfxConfig {
  /** Frame offset for scene transition SFX (7 frames before scene start) */
  transition?: SfxCategory;
  /** Element reveal SFX at specific frame offsets within the scene */
  reveals?: Array<{frame: number; type: SfxCategory; volume?: number}>;
  /** Riser before climax moment */
  riser?: {startFrame: number; endFrame: number};
}

/**
 * Build SFX sequences for a full video.
 * @param scenes - Array of {dur, sfx?} per scene
 * @returns React elements to place inside the composition
 */
export function buildSfxLayer(
  scenes: Array<{dur: number; sfx?: SceneSfxConfig}>,
): React.ReactElement[] {
  const elements: React.ReactElement[] = [];
  let offset = 0;
  let sfxIdx = 0;

  scenes.forEach((scene, sceneIdx) => {
    const sfx = scene.sfx;

    // Auto transition SFX: whoosh 7 frames before scene start (except first scene)
    if (sceneIdx > 0) {
      const transType = sfx?.transition || "whoosh";
      const transFrame = Math.max(0, offset - 7);
      elements.push(
        <Sequence key={`trans-${sceneIdx}`} from={transFrame} durationInFrames={30}>
          <Audio src={staticFile(pick(transType, sfxIdx++))} volume={0.35} />
        </Sequence>
      );
    }

    // Custom reveals within scene
    if (sfx?.reveals) {
      sfx.reveals.forEach((reveal, revIdx) => {
        elements.push(
          <Sequence key={`reveal-${sceneIdx}-${revIdx}`} from={offset + reveal.frame} durationInFrames={30}>
            <Audio src={staticFile(pick(reveal.type, sfxIdx++))} volume={reveal.volume ?? 0.3} />
          </Sequence>
        );
      });
    }

    // Riser (tension build-up)
    if (sfx?.riser) {
      elements.push(
        <Sequence key={`riser-${sceneIdx}`} from={offset + sfx.riser.startFrame} durationInFrames={sfx.riser.endFrame - sfx.riser.startFrame + 15}>
          <Audio src={staticFile(pick("riser", sfxIdx++))} volume={0.25} />
        </Sequence>
      );
    }

    offset += scene.dur;
  });

  return elements;
}

/**
 * Quick auto-SFX: generates sensible defaults for a standard 5-scene video.
 * Scene 1: impact on hook, Scene 2-4: whoosh + pop reveals, Scene 5: shimmer CTA
 */
export function autoSfx(
  scenes: Array<{dur: number}>,
): React.ReactElement[] {
  const enriched = scenes.map((scene, i) => {
    const sfx: SceneSfxConfig = {};

    if (i === 0) {
      // Hook: impact at start
      sfx.reveals = [{frame: 5, type: "impact", volume: 0.4}];
    } else if (i === scenes.length - 1) {
      // CTA: shimmer + notification
      sfx.transition = "whoosh";
      sfx.reveals = [
        {frame: 10, type: "shimmer", volume: 0.3},
        {frame: 50, type: "notification", volume: 0.35},
      ];
    } else {
      // Middle scenes: whoosh transition + 2 pop reveals
      sfx.transition = "whoosh";
      sfx.reveals = [
        {frame: 15, type: "pop", volume: 0.3},
        {frame: Math.floor(scene.dur * 0.5), type: "pop", volume: 0.25},
      ];
      // Add riser in penultimate scene
      if (i === scenes.length - 2) {
        sfx.riser = {startFrame: Math.floor(scene.dur * 0.6), endFrame: scene.dur - 5};
      }
    }

    return {...scene, sfx};
  });

  return buildSfxLayer(enriched);
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
